import { Link } from "@tanstack/react-router";
import type { RichBlogPost, RichSection } from "@/lib/blog/rich-types";
import {
  Copy,
  CheckCircle2,
  Info,
  AlertTriangle,
  Terminal,
  Sparkles,
  Clock,
  User,
  Calendar,
} from "lucide-react";
import { useState } from "react";

function CopyChip({ code }: { code: string }) {
  const [copied, setCopied] = useState(false);
  return (
    <button
      type="button"
      onClick={async () => {
        try {
          await navigator.clipboard.writeText(code);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        } catch {
          /* ignore */
        }
      }}
      className="inline-flex items-center gap-1.5 rounded-md bg-primary/10 px-2.5 py-1 text-xs font-medium text-primary hover:bg-primary/20"
      aria-label="Copy code"
    >
      {copied ? <CheckCircle2 className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
      {copied ? "Copied" : "Copy"}
    </button>
  );
}

function CodeBadge({ code }: { code: string }) {
  return (
    <div className="mt-3 flex items-center justify-between rounded-lg border border-primary/30 bg-primary/5 px-3 py-2">
      <code className="font-mono text-base font-bold tracking-wider text-primary">{code}</code>
      <CopyChip code={code} />
    </div>
  );
}

function slugify(s: string) {
  return s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
}

function SectionBlock({ s, i }: { s: RichSection; i: number }) {
  const anchor = "id" in s && s.id ? s.id : "heading" in s && s.heading ? slugify(s.heading) : `sec-${i}`;

  switch (s.type) {
    case "paragraph": {
      const paras = Array.isArray(s.text) ? s.text : [s.text];
      return (
        <section id={anchor} className="scroll-mt-24">
          {s.heading && <h2 className="mt-8 mb-3 text-2xl font-bold tracking-tight">{s.heading}</h2>}
          {paras.map((p, j) => (
            <p key={j} className="mt-3 leading-relaxed text-foreground/85">{p}</p>
          ))}
        </section>
      );
    }
    case "callout": {
      const map = {
        info: { icon: Info, cls: "border-sky-500/40 bg-sky-500/5 text-sky-500" },
        success: { icon: CheckCircle2, cls: "border-emerald-500/40 bg-emerald-500/5 text-emerald-500" },
        warning: { icon: AlertTriangle, cls: "border-amber-500/40 bg-amber-500/5 text-amber-500" },
        code: { icon: Terminal, cls: "border-primary/40 bg-primary/5 text-primary" },
      };
      const v = map[s.variant ?? "info"];
      const Icon = v.icon;
      return (
        <aside className={`mt-6 rounded-xl border-l-4 p-4 ${v.cls}`}>
          <div className="flex items-start gap-3">
            <Icon className="mt-0.5 h-5 w-5 shrink-0" />
            <div className="flex-1 text-foreground">
              {s.title && <div className="font-semibold">{s.title}</div>}
              <p className="mt-1 text-sm leading-relaxed text-foreground/85">{s.body}</p>
              {s.code && <CodeBadge code={s.code} />}
            </div>
          </div>
        </aside>
      );
    }
    case "cards": {
      return (
        <section id={anchor} className="scroll-mt-24 mt-10">
          {s.heading && <h2 className="mb-2 text-2xl font-bold tracking-tight">{s.heading}</h2>}
          {s.intro && <p className="mb-5 text-foreground/80">{s.intro}</p>}
          <div className="grid gap-4 sm:grid-cols-2">
            {s.cards.map((c, j) => (
              <div key={j} className="rounded-xl border border-border bg-card p-5 shadow-sm transition-all hover:border-primary/50 hover:shadow-md">
                {c.badge && (
                  <span className="mb-2 inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-primary">
                    <Sparkles className="h-3 w-3" /> {c.badge}
                  </span>
                )}
                <h3 className="text-lg font-bold leading-snug">{c.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/80">{c.body}</p>
                {c.code && <CodeBadge code={c.code} />}
                {c.url && (
                  <a href={c.url} className="mt-3 inline-block text-xs font-semibold text-primary hover:underline">
                    Learn more →
                  </a>
                )}
              </div>
            ))}
          </div>
        </section>
      );
    }
    case "steps": {
      return (
        <section id={anchor} className="scroll-mt-24 mt-10">
          {s.heading && <h2 className="mb-2 text-2xl font-bold tracking-tight">{s.heading}</h2>}
          {s.intro && <p className="mb-5 text-foreground/80">{s.intro}</p>}
          <ol className="space-y-4">
            {s.steps.map((st, j) => (
              <li key={j} className="flex gap-4 rounded-xl border border-border bg-card/60 p-5">
                <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-primary font-bold text-primary-foreground">
                  {j + 1}
                </div>
                <div className="flex-1">
                  <div className="font-semibold">{st.title}</div>
                  <p className="mt-1 text-sm leading-relaxed text-foreground/80">{st.body}</p>
                  {st.code && <CodeBadge code={st.code} />}
                </div>
              </li>
            ))}
          </ol>
        </section>
      );
    }
    case "table": {
      return (
        <section id={anchor} className="scroll-mt-24 mt-10">
          {s.heading && <h2 className="mb-2 text-2xl font-bold tracking-tight">{s.heading}</h2>}
          {s.intro && <p className="mb-4 text-foreground/80">{s.intro}</p>}
          <div className="overflow-x-auto rounded-lg border border-border">
            <table className="w-full text-sm">
              <thead className="bg-muted text-xs uppercase">
                <tr>{s.headers.map((h, j) => <th key={j} className="px-3 py-2 text-left font-semibold">{h}</th>)}</tr>
              </thead>
              <tbody className="divide-y divide-border">
                {s.rows.map((row, ri) => (
                  <tr key={ri} className={ri % 2 ? "bg-muted/30" : ""}>
                    {row.map((cell, ci) => <td key={ci} className="px-3 py-2">{cell}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </section>
      );
    }
    case "quote":
      return (
        <blockquote className="mt-6 rounded-r-xl border-l-4 border-primary bg-primary/5 p-5 italic text-foreground/90">
          "{s.body}"
          {s.author && <div className="mt-2 text-xs font-semibold not-italic text-muted-foreground">— {s.author}</div>}
        </blockquote>
      );
    case "codeBlock":
      return (
        <section id={anchor} className="scroll-mt-24 mt-8">
          {s.heading && <h2 className="mb-2 text-2xl font-bold tracking-tight">{s.heading}</h2>}
          <div className="rounded-xl border border-primary/30 bg-primary/5 p-5">
            {s.label && <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{s.label}</div>}
            <div className="flex items-center justify-between">
              <code className="font-mono text-2xl font-bold tracking-wider text-primary">{s.code}</code>
              <CopyChip code={s.code} />
            </div>
            {s.note && <p className="mt-3 text-xs text-muted-foreground">{s.note}</p>}
          </div>
        </section>
      );
    case "faq":
      return (
        <section id={anchor} className="scroll-mt-24 mt-10">
          {s.heading && <h2 className="mb-3 text-2xl font-bold tracking-tight">{s.heading}</h2>}
          <div className="space-y-3">
            {s.items.map((it, j) => (
              <details key={j} className="group rounded-xl border border-border bg-card p-4 open:shadow-sm">
                <summary className="cursor-pointer list-none font-semibold marker:hidden">
                  <span className="text-primary">Q. </span>{it.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">{it.a}</p>
              </details>
            ))}
          </div>
        </section>
      );
  }
}

export function RichBlogRenderer({ post }: { post: RichBlogPost }) {
  const tocItems = post.sections
    .map((s, i) => {
      const heading = "heading" in s ? s.heading : undefined;
      if (!heading) return null;
      const id = "id" in s && s.id ? s.id : slugify(heading);
      return { id, heading };
    })
    .filter(Boolean) as { id: string; heading: string }[];

  return (
    <article className="mx-auto max-w-4xl px-4 py-8">
      {/* HERO CARD */}
      <div className="rounded-2xl border border-border bg-gradient-to-br from-card to-muted/50 p-6 md:p-8 shadow-sm">
        <nav className="text-xs text-muted-foreground mb-3">
          <Link to="/" className="hover:text-primary">Home</Link> ›{" "}
          <Link to="/blog" className="hover:text-primary">Blog</Link>
          {post.category && <> › <span>{post.category}</span></>}
        </nav>
        {post.heroBadge && (
          <span className="mb-3 inline-flex items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wide text-primary">
            <Sparkles className="h-3 w-3" /> {post.heroBadge}
          </span>
        )}
        <h1 className="text-3xl md:text-4xl font-black leading-tight tracking-tight">{post.title}</h1>
        {post.heroTagline && <p className="mt-3 text-lg text-muted-foreground">{post.heroTagline}</p>}
        <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
          {post.author && (
            <span className="inline-flex items-center gap-1"><User className="h-3.5 w-3.5" /> {post.author.name}</span>
          )}
          <span className="inline-flex items-center gap-1">
            <Calendar className="h-3.5 w-3.5" />
            {new Date(post.date).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" })}
            {post.updated && ` · updated ${new Date(post.updated).toLocaleDateString("en", { day: "numeric", month: "short", year: "numeric" })}`}
          </span>
          <span className="inline-flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> {post.readMinutes} min read</span>
        </div>
      </div>

      {/* TL;DR */}
      {post.tldr && post.tldr.length > 0 && (
        <aside className="mt-6 rounded-xl border-l-4 border-primary bg-primary/5 p-5">
          <div className="mb-2 flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-primary">
            <Sparkles className="h-4 w-4" /> Quick Answer (TL;DR)
          </div>
          <ul className="space-y-1.5 text-sm leading-relaxed text-foreground/90">
            {post.tldr.map((t, j) => (
              <li key={j} className="flex gap-2">
                <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-primary" />
                <span>{t}</span>
              </li>
            ))}
          </ul>
        </aside>
      )}

      {/* TOC */}
      {tocItems.length >= 3 && (
        <nav className="mt-6 rounded-xl border border-border bg-card/60 p-4">
          <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-muted-foreground">On this page</div>
          <div className="flex flex-wrap gap-2">
            {tocItems.map((t) => (
              <a key={t.id} href={`#${t.id}`} className="rounded-full border border-border bg-background px-3 py-1 text-xs hover:border-primary hover:text-primary">
                {t.heading}
              </a>
            ))}
          </div>
        </nav>
      )}

      {/* SECTIONS */}
      <div className="mt-4">
        {post.sections.map((s, i) => <SectionBlock key={i} s={s} i={i} />)}
      </div>

      {/* FAQ */}
      {post.faq && post.faq.length > 0 && (
        <section id="faq" className="scroll-mt-24 mt-12">
          <h2 className="mb-3 text-2xl font-bold tracking-tight">Frequently Asked Questions</h2>
          <div className="space-y-3">
            {post.faq.map((it, j) => (
              <details key={j} className="group rounded-xl border border-border bg-card p-4 open:shadow-sm">
                <summary className="cursor-pointer list-none font-semibold marker:hidden">
                  <span className="text-primary">Q. </span>{it.q}
                </summary>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">{it.a}</p>
              </details>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      {post.cta && (
        <div className="mt-12 rounded-2xl border border-primary/40 bg-gradient-to-br from-primary/10 to-primary/5 p-6">
          <div className="text-lg font-bold">{post.cta.title}</div>
          <p className="mt-1 text-sm text-foreground/80">{post.cta.body}</p>
          <a
            href={post.cta.buttonUrl}
            className="mt-4 inline-block rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
          >
            {post.cta.buttonText}
          </a>
        </div>
      )}

      {/* RELATED */}
      {post.related && post.related.length > 0 && (
        <section className="mt-12">
          <h2 className="mb-3 text-xl font-bold">Related guides</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {post.related.map((r, j) => (
              <a key={j} href={r.url} className="block rounded-xl border border-border bg-card p-4 hover:border-primary/50">
                <div className="font-semibold text-primary">{r.title}</div>
                {r.note && <div className="mt-1 text-xs text-muted-foreground">{r.note}</div>}
              </a>
            ))}
          </div>
        </section>
      )}
    </article>
  );
}

/** Build JSON-LD (Article + optional FAQPage/HowTo). */
export function buildRichBlogSchema(post: RichBlogPost, url: string): { type: string; children: string }[] {
  const scripts: { type: string; children: string }[] = [];
  scripts.push({
    type: "application/ld+json",
    children: JSON.stringify({
      "@context": "https://schema.org",
      "@type": "Article",
      headline: post.title,
      description: post.description,
      datePublished: post.date,
      dateModified: post.updated ?? post.date,
      author: { "@type": "Person", name: post.author?.name ?? "DrHint Editorial" },
      publisher: { "@type": "Organization", name: "DrHint", url: "https://drhint.com" },
      mainEntityOfPage: url,
      keywords: post.keywords.join(", "),
    }),
  });
  if (post.faq && post.faq.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: post.faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    });
  }
  if (post.schema?.type === "HowTo" && post.schema.howToSteps && post.schema.howToSteps.length > 0) {
    scripts.push({
      type: "application/ld+json",
      children: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "HowTo",
        name: post.title,
        step: post.schema.howToSteps.map((st, i) => ({
          "@type": "HowToStep",
          position: i + 1,
          name: st.name,
          text: st.text,
        })),
      }),
    });
  }
  return scripts;
}